import { defineColumns } from './piColumns.js';
import { EMPTY, formatDateFull, formatDelta, formatInteger, formatPercent, isNumber } from './piFormat.js';
import { indicatorValue } from './piOverviewModel.js';
import { MODULE_OPTIONS, ROLE_CLASS_OPTIONS, SIZE_BAND_OPTIONS, optionLabel, roleClassLabels, sortRows } from './piQuery.js';
import { STATE_INFO, stateDescription, stateLabel, stateTone } from './piState.js';
import { companyRoute, featureRoute } from './piNavigation.js';
import { listOf, objectOf } from './piRequests.js';
import { countText, metricCell, numberOf, paddedKey, ratioText, shareOf } from './piTable.js';

export const CLASSIFICATIONS = [
  { value: 'opportunity', label: 'Liberada e pouco usada', tone: 'warn', description: 'Liberada em muitas empresas e pouco usada.' },
  { value: 'growing', label: 'Em crescimento', tone: 'ok', description: 'Adoção subiu em relação ao período anterior.' },
  { value: 'declining', label: 'Em queda', tone: 'bad', description: 'Adoção caiu em relação ao período anterior.' },
  { value: 'stable', label: 'Estável', tone: 'muted', description: 'Sem variação relevante em relação ao período anterior.' },
];

export const NO_CLASSIFICATION = {
  value: 'none',
  label: 'Sem classificação',
  tone: 'muted',
  description: 'Sem base para classificar: estado do indicador, amostra pequena ou sem período anterior.',
};

export const FEATURE_STATE_ORDER = ['ok', 'partial', 'derived', 'no_usage', 'insufficient', 'no_eligible', 'disabled', 'not_instrumented', 'na'];

const cellMetric = (field) => (row) => {
  const cell = objectOf(row)[field];
  return cell && cell.metric ? cell.metric : null;
};

const configuredValue = (field) => (row) => {
  const configured = objectOf(row).configured;
  return configured && configured[field] !== undefined ? configured[field] : null;
};

export const FEATURE_COLUMNS = defineColumns([
  { key: 'name', label: 'Funcionalidade', type: 'text', infoKey: 'features.name', sortable: true, startDescending: false, width: 36 },
  { key: 'featureType', label: 'Número', type: 'int', infoKey: 'features.featureType', screen: false },
  { key: 'moduleText', label: 'Módulo', type: 'text', infoKey: 'features.module', screen: false, value: (row) => (row.moduleText === EMPTY ? '' : row.moduleText) },
  { key: 'eligibleCompanies', label: 'Empresas elegíveis', type: 'int', infoKey: 'features.eligibleCompanies', sortable: true, startDescending: true },
  {
    key: 'configuredCompanies', label: 'Empresas com a funcionalidade configurada', type: 'int', infoKey: 'features.configured', screen: false,
    value: configuredValue('companies'),
  },
  { key: 'configuredUsers', label: 'Quantidade configurada', type: 'int', infoKey: 'features.configured', screen: false, value: configuredValue('users') },
  { key: 'configuredUnit', label: 'Unidade da quantidade configurada', type: 'text', infoKey: 'features.configured', screen: false, value: configuredValue('unit') },
  { key: 'adoptingCompanies', label: 'Empresas que usaram', type: 'int', infoKey: 'features.adoptingCompanies', sortable: true, startDescending: true },
  {
    key: 'companyAdoption', label: 'Empresas que usaram (% das elegíveis)', type: 'metric', unit: 'pct', infoKey: 'features.companyAdoption', screen: false,
    value: cellMetric('companyAdoption'),
  },
  { key: 'eligibleUsers', label: 'Usuários elegíveis', type: 'int', infoKey: 'features.eligibleUsers', sortable: true, startDescending: true },
  { key: 'adoptingUsers', label: 'Usuários ativos', type: 'int', infoKey: 'features.adoptingUsers', sortable: true, startDescending: true },
  {
    key: 'adoptionValue', label: 'Adoção', type: 'metric', unit: 'pct', infoKey: 'features.adoption', sortable: true, startDescending: true,
    value: cellMetric('adoption'),
  },
  { key: 'adoptionDelta', label: 'Δ adoção', type: 'decimal', infoKey: 'features.adoptionDelta', sortable: true, startDescending: true },
  {
    key: 'recurrenceValue', label: 'Recorrência', type: 'metric', unit: 'pct', infoKey: 'features.recurrence', sortable: true, startDescending: true,
    value: cellMetric('recurrence'),
  },
  {
    key: 'frequencyValue', label: 'Frequência', type: 'metric', unit: 'ratio', infoKey: 'features.frequency', sortable: true, startDescending: true,
    value: cellMetric('frequency'),
  },
  {
    key: 'intensityValue', label: 'Intensidade', type: 'metric', unit: 'days', infoKey: 'features.intensity', sortable: true, startDescending: true,
    value: cellMetric('intensity'),
  },
  {
    key: 'successValue', label: 'Sucesso', type: 'metric', unit: 'pct', infoKey: 'features.successRate', sortable: true, startDescending: true,
    value: cellMetric('successRate'),
  },
  {
    key: 'classificationOrder', label: 'Classificação', type: 'text', infoKey: 'features.classification', sortable: true, startDescending: false,
    value: (row) => objectOf(objectOf(row).classification).label,
  },
  { key: 'trend', label: 'Tendência', type: 'text', infoKey: 'common.trend', export: false },
  { key: 'state', label: 'Estado', type: 'text', infoKey: 'features.state', value: (row) => objectOf(objectOf(row).state).label },
  { key: 'stateReason', label: 'Motivo do estado', type: 'text', infoKey: 'features.stateReason', screen: false, value: (row) => objectOf(objectOf(row).adoption).reason },
]);

export const CLASSIFICATION_INFO_PREFIX = 'features.classification.';

export const classificationInfoKey = (value) => CLASSIFICATION_INFO_PREFIX + classificationInfo(value).value;

export const DEFAULT_FEATURE_SORT = { sort: 'eligibleUsers', desc: true };

const FEATURE_PARAM = /^\d{1,4}$/;

const textOf = (value) => (value === null || value === undefined ? '' : String(value));

export const classificationInfo = (value) => {
  const key = textOf(value).toLowerCase();
  for (let i = 0; i < CLASSIFICATIONS.length; i++) {
    if (CLASSIFICATIONS[i].value === key) return CLASSIFICATIONS[i];
  }
  return NO_CLASSIFICATION;
};

export const classificationOrder = (value) => {
  const info = classificationInfo(value);
  const index = CLASSIFICATIONS.indexOf(info);
  return index === -1 ? CLASSIFICATIONS.length : index;
};

export const moduleName = (module) => {
  if (module === null || module === undefined || module === '') return EMPTY;
  if (isNumber(module) || /^\d+$/.test(textOf(module))) return optionLabel(MODULE_OPTIONS, String(Number(module)));
  return textOf(module);
};

export const featureStateInfo = (state) => {
  const key = Object.prototype.hasOwnProperty.call(STATE_INFO, state) ? state : 'na';
  return { value: key, label: stateLabel(key), tone: stateTone(key), description: stateDescription(key) };
};

export const configuredState = (value) => {
  const source = value && typeof value === 'object' ? value : null;
  if (!source || !isNumber(source.companies)) return null;
  const companies = source.companies;
  const users = isNumber(source.users) ? source.users : null;
  const unit = textOf(source.usersUnit);
  const companiesText = formatInteger(companies) + (companies === 1 ? ' empresa' : ' empresas');
  const usersText = users !== null && unit ? formatInteger(users) + ' ' + unit : '';
  return {
    companies: companies,
    users: users,
    unit: users !== null ? unit : '',
    text: 'Configurada em ' + companiesText + (usersText ? ' · ' + usersText : ''),
    asOfText: source.asOf ? formatDateFull(source.asOf) : '',
  };
};

export const featureRow = (row, compare) => {
  const source = row || {};
  const adoption = metricCell(source.adoption, {});
  const recurrence = metricCell(source.recurrence, { compare: compare, ratio: false });
  const frequency = metricCell(source.frequency, { compare: compare, ratio: false });
  const intensity = metricCell(source.intensity, { compare: compare, ratio: false });
  const successRate = metricCell(source.successRate, { compare: compare });
  const state = Object.prototype.hasOwnProperty.call(STATE_INFO, source.state) ? source.state : adoption.metric.state;
  const classification = classificationInfo(source.classification);
  const delta = compare && adoption.visible && isNumber(adoption.metric.deltaAbs) ? adoption.metric.deltaAbs : null;
  const name = textOf(source.name) || ('Funcionalidade ' + textOf(source.featureType));
  const moduleText = moduleName(source.module);
  return {
    key: textOf(source.featureType) || name,
    featureType: isNumber(source.featureType) ? source.featureType : (FEATURE_PARAM.test(textOf(source.featureType)) ? Number(source.featureType) : null),
    name: name,
    route: featureRoute(source.featureType),
    moduleText: moduleText,
    category: textOf(source.category),
    eligibleCompanies: numberOf(source.eligibleCompanies),
    adoptingCompanies: numberOf(source.adoptingCompanies),
    eligibleUsers: numberOf(source.eligibleUsers),
    adoptingUsers: numberOf(source.adoptingUsers),
    adoption: adoption,
    companyAdoption: metricCell(source.companyAdoption, { compare: compare }),
    recurrence: recurrence,
    frequency: frequency,
    intensity: intensity,
    successRate: successRate,
    adoptionValue: adoption.sortValue,
    adoptionDelta: delta,
    adoptionDeltaText: delta === null ? EMPTY : formatDelta(delta, null, 'pct'),
    recurrenceValue: recurrence.sortValue,
    frequencyValue: frequency.sortValue,
    intensityValue: intensity.sortValue,
    successValue: successRate.sortValue,
    trend: listOf(source.trend),
    classification: classification,
    classificationOrder: classificationOrder(source.classification),
    state: featureStateInfo(state),
    configured: configuredState(source.configured),
    searchText: (name + ' ' + moduleText + ' ' + textOf(source.featureType) + ' ' + textOf(source.category)).toLowerCase(),
  };
};

export const featureRows = (rows, compare) => listOf(rows).map((row) => featureRow(row, compare));

export const filterFeatureRows = (rows, criteria) => {
  const settings = criteria || {};
  const term = textOf(settings.search).replace(/\s+/g, ' ').trim().toLowerCase();
  return listOf(rows).filter((row) => {
    if (term && row.searchText.indexOf(term) === -1) return false;
    if (settings.classification && row.classification.value !== settings.classification) return false;
    if (settings.state && row.state.value !== settings.state) return false;
    return true;
  });
};

export const visibleFeatureRows = (rows, criteria, sort) => {
  const state = sort || DEFAULT_FEATURE_SORT;
  return sortRows(filterFeatureRows(rows, criteria), state.sort, state.desc);
};

export const classificationSummary = (rows) => {
  const list = listOf(rows);
  const counts = {};
  list.forEach((row) => {
    counts[row.classification.value] = (counts[row.classification.value] || 0) + 1;
  });
  return CLASSIFICATIONS.concat([NO_CLASSIFICATION]).map((info) => ({
    value: info.value,
    label: info.label,
    tone: info.tone,
    description: info.description,
    count: counts[info.value] || 0,
  }));
};

export const stateFilterOptions = (rows) => {
  const present = {};
  listOf(rows).forEach((row) => {
    present[row.state.value] = (present[row.state.value] || 0) + 1;
  });
  return FEATURE_STATE_ORDER.filter((state) => present[state]).map((state) => ({
    value: state,
    label: stateLabel(state) + ' (' + present[state] + ')',
  }));
};

export const parseFeatureParam = (value) => {
  const raw = textOf(value).trim();
  if (!FEATURE_PARAM.test(raw)) return null;
  return String(Number(raw));
};

const breakdownRows = (rows, options) => listOf(rows).map((row, index) => {
  const label = textOf(row.label) || (options ? optionLabel(options, textOf(row.key)) : textOf(row.key)) || 'Sem valor';
  return {
    key: textOf(row.key) || String(index),
    label: label,
    adoption: metricCell(row.adoption || row.value, {}),
    adoptingUsers: numberOf(row.adoptingUsers),
    eligibleUsers: numberOf(row.eligibleUsers),
    usersText: ratioText(numberOf(row.adoptingUsers), numberOf(row.eligibleUsers)),
  };
});

const SIZE_LABEL = /^Porte ([A-E])$/;

export const companyCutRows = (rows) => {
  const sizes = [];
  const profiles = [];
  listOf(rows).forEach((row, index) => {
    const value = textOf(row.key);
    const match = SIZE_LABEL.exec(textOf(row.label));
    const size = !!match;
    const key = size ? match[1] : value;
    const entry = {
      key: (size ? 'size:' : 'profile:') + (key || String(index)),
      cut: size ? 'Porte' : 'Perfil da empresa',
      label: size ? optionLabel(SIZE_BAND_OPTIONS, key) : (key ? 'Perfil ' + key : 'Sem perfil'),
      adoption: metricCell(row.adoption || row.value, {}),
      adoptingUsers: numberOf(row.adoptingUsers),
      eligibleUsers: numberOf(row.eligibleUsers),
      usersText: ratioText(numberOf(row.adoptingUsers), numberOf(row.eligibleUsers)),
    };
    (size ? sizes : profiles).push(entry);
  });
  return sizes.concat(profiles);
};

export const DETAIL_CUT_FIELDS = ['roleClass', 'sizeBand', 'segment'];

export const DETAIL_MULTI_CUT_REASON = 'a adoção do topo aceita um corte por vez (perfil, porte ou segmento); deixe só um para ver o recorte';

const KEPT_ADOPTION_STATES = ['na', 'not_instrumented'];

export const TOP_COMPANIES_HINT = 'Usuários que usaram a funcionalidade no dia de maior uso de cada empresa nos 30 dias; o % é sobre os elegíveis de hoje ' +
  '(usuários habilitados agora com papel que usa a funcionalidade). Favorece empresa grande: para aderência, use Empresas por adoção.';

const TOP_COMPANIES_NOTE = 'Empresas com mais uso:';

export const featureDetailEnvelope = (envelope) => {
  if (!envelope || typeof envelope !== 'object') return envelope;
  const notes = listOf(envelope.notes).filter((note) => String(note).indexOf(TOP_COMPANIES_NOTE) !== 0);
  return Object.assign({}, envelope, { notes: notes });
};

export const detailCutCount = (params) => {
  const source = objectOf(params);
  return DETAIL_CUT_FIELDS.filter((key) => textOf(source[key]) !== '').length;
};

export const detailAdoption = (adoption, params) => {
  const metric = adoption && typeof adoption === 'object' ? adoption : null;
  if (detailCutCount(params) <= 1) return metric;
  if (metric && KEPT_ADOPTION_STATES.indexOf(metric.state) !== -1) return metric;
  return { value: null, unit: 'pct', state: 'na', stateReason: DETAIL_MULTI_CUT_REASON, definitionKey: 'feature_adoption' };
};

export const featureDetailView = (data, compare) => {
  const source = objectOf(data);
  const feature = objectOf(source.feature);
  const series = listOf(source.series)
    .filter((point) => typeof point.date === 'string' && point.date)
    .slice()
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const actions = listOf(source.actions);
  const totalEvents = actions.reduce((sum, action) => sum + (isNumber(action.events) ? action.events : 0), 0);
  const retention = listOf(source.retention)
    .filter((cell) => isNumber(cell.week))
    .slice()
    .sort((a, b) => a.week - b.week);
  return {
    feature: {
      featureType: feature.featureType,
      name: textOf(feature.displayName) || textOf(feature.name) || '',
      technicalName: textOf(feature.name),
      moduleText: moduleName(feature.module),
      category: textOf(feature.category),
      rolesText: roleClassLabels(feature.rolesApplicable).join(', '),
      expectedFrequency: textOf(feature.expectedFrequency),
      gatedText: feature.gated === true ? 'Liberada por empresa' : (feature.gated === false ? 'Disponível para todas as empresas' : ''),
    },
    adoption: source.adoption && typeof source.adoption === 'object' ? source.adoption : null,
    configured: configuredState(source.configured),
    depth: source.depth && typeof source.depth === 'object' ? source.depth : null,
    timeToFirstUse: source.timeToFirstUse && typeof source.timeToFirstUse === 'object' ? source.timeToFirstUse : null,
    adoptionSeries: [{
      key: 'adoption',
      label: 'Adoção',
      area: true,
      points: series.map((point) => ({ date: point.date, value: numberOf(point.adoption), partial: point.isPartial === true })),
    }],
    usersSeries: [
      { key: 'adoptingUsers', label: 'Usuários que usaram', points: series.map((point) => ({ date: point.date, value: numberOf(point.adoptingUsers), partial: point.isPartial === true })) },
      { key: 'eligibleUsers', label: 'Usuários elegíveis', points: series.map((point) => ({ date: point.date, value: numberOf(point.eligibleUsers), partial: point.isPartial === true })) },
    ],
    seriesRows: series,
    byRoleClass: breakdownRows(source.byRoleClass, ROLE_CLASS_OPTIONS),
    bySegment: companyCutRows(source.bySegment),
    topCompanies: listOf(source.topCompanies).map((company, index) => ({
      key: textOf(company.shardCode) + ':' + (textOf(company.companyId) || String(index)),
      companyId: textOf(company.companyId),
      name: textOf(company.name) || textOf(company.companyId) || 'Empresa',
      shardCode: textOf(company.shardCode),
      route: companyRoute(company.companyId, company.shardCode),
      adoptingUsers: numberOf(company.adoptingUsers),
      eligibleUsers: numberOf(company.eligibleUsers),
      usersText: ratioText(numberOf(company.adoptingUsers), numberOf(company.eligibleUsers)),
      adoption: metricCell(company.adoption, { compare: compare }),
    })),
    actions: actions.map((action, index) => {
      const share = shareOf(action.events, totalEvents);
      return {
        key: textOf(action.eventName) || String(index),
        label: textOf(action.label) || textOf(action.eventName) || 'Ação',
        eventName: textOf(action.eventName),
        value: isNumber(action.users) ? action.users : null,
        users: isNumber(action.users) ? action.users : null,
        events: isNumber(action.events) ? action.events : null,
        share: share,
        valueText: countText(action.users) + (action.users === 1 ? ' usuário' : ' usuários') + ' · ' + countText(action.events) +
          ' eventos' + (share === null ? '' : ' (' + formatPercent(share) + ')'),
        title: textOf(action.eventName),
      };
    }),
    retention: retention.map((cell) => ({
      week: cell.week,
      label: 'W' + cell.week,
      matured: cell.matured === true,
      pct: cell.matured === true && isNumber(cell.pct) ? cell.pct : null,
      text: cell.matured === true ? (isNumber(cell.pct) ? formatPercent(cell.pct) : 'n/d') : 'pendente',
    })),
    retentionSeries: [{
      key: 'retention',
      label: 'Retenção de quem fez o primeiro uso',
      points: retention.map((cell) => ({
        date: paddedKey(cell.week),
        value: cell.matured === true && isNumber(cell.pct) ? cell.pct : null,
      })),
    }],
    eventCount: isNumber(totalEvents) ? formatInteger(totalEvents) : EMPTY,
  };
};

const sortLabel = (sort) => {
  const state = objectOf(sort);
  const column = FEATURE_COLUMNS.filter((item) => item.key === state.sort)[0];
  return column ? 'Ordem: ' + column.label + (state.desc ? ', decrescente' : ', crescente') : '';
};

export const featureListLines = (criteria, sort) => {
  const settings = objectOf(criteria);
  const lines = [];
  const term = textOf(settings.search).replace(/\s+/g, ' ').trim();
  if (term) lines.push('Buscar funcionalidade: "' + term + '"');
  if (settings.classification) lines.push('Classificação: ' + classificationInfo(settings.classification).label);
  if (settings.state) lines.push('Estado: ' + stateLabel(settings.state));
  const order = sortLabel(sort || DEFAULT_FEATURE_SORT);
  if (order) lines.push(order);
  return lines;
};

export const FEATURE_DETAIL_KPI_COLUMNS = defineColumns([
  { key: 'period', label: 'Período', type: 'text', infoKey: 'filter.period', screen: false },
  { key: 'adoption', label: 'Adoção (usuários)', type: 'metric', unit: 'pct', infoKey: 'features.detailAdoption', value: indicatorValue('adoption') },
  { key: 'depth', label: 'Profundidade', type: 'metric', unit: 'pct', infoKey: 'features.depth', value: indicatorValue('depth') },
  { key: 'timeToFirstUse', label: 'Tempo até o 1º uso', type: 'metric', unit: 'days', infoKey: 'features.timeToFirstUse', value: indicatorValue('timeToFirstUse') },
]);

export const FEATURE_SERIES_COLUMNS = defineColumns([
  { key: 'date', label: 'Data', type: 'date', infoKey: 'features.seriesDate' },
  { key: 'adoption', label: 'Adoção', type: 'percent', infoKey: 'features.seriesAdoption', value: (point) => numberOf(objectOf(point).adoption) },
  { key: 'adoptingUsers', label: 'Usuários que usaram', type: 'int', infoKey: 'features.seriesAdopting', value: (point) => numberOf(objectOf(point).adoptingUsers) },
  { key: 'eligibleUsers', label: 'Usuários elegíveis', type: 'int', infoKey: 'features.seriesEligible', value: (point) => numberOf(objectOf(point).eligibleUsers) },
  { key: 'isPartial', label: 'Parcial', type: 'text', infoKey: 'common.state.partial', value: (point) => objectOf(point).isPartial === true },
]);

const CUT_MEASURES = [
  { key: 'users', label: 'Usuários', type: 'text', infoKey: 'features.cutUsers', export: false, exportedAs: ['adoptingUsers', 'eligibleUsers'] },
  { key: 'adoptingUsers', label: 'Usuários que usaram', type: 'int', infoKey: 'features.cutAdopting', screen: false },
  { key: 'eligibleUsers', label: 'Usuários elegíveis', type: 'int', infoKey: 'features.cutEligible', screen: false },
  { key: 'adoption', label: 'Adoção', type: 'metric', unit: 'pct', infoKey: 'features.cutAdoption', value: cellMetric('adoption') },
];

export const FEATURE_ROLE_COLUMNS = defineColumns([
  { key: 'label', label: 'Papel', type: 'text', infoKey: 'features.cutRole' },
].concat(CUT_MEASURES));

export const FEATURE_SEGMENT_COLUMNS = defineColumns([
  { key: 'cut', label: 'Corte', type: 'text', infoKey: 'features.cutKind', screen: false },
  { key: 'label', label: 'Porte / perfil', type: 'text', infoKey: 'features.cutSizeProfile' },
].concat(CUT_MEASURES));

export const FEATURE_ACTION_COLUMNS = defineColumns([
  { key: 'label', label: 'Ação', type: 'text', infoKey: 'features.action', width: 36 },
  { key: 'eventName', label: 'Evento', type: 'text', infoKey: 'features.actionEvent', screen: false },
  { key: 'users', label: 'Usuários', type: 'int', infoKey: 'features.actionUsers' },
  { key: 'events', label: 'Eventos', type: 'int', infoKey: 'features.actionEvents' },
  { key: 'share', label: '% dos eventos', type: 'percent', infoKey: 'features.actionShare' },
]);

export const FEATURE_TOP_COMPANY_COLUMNS = defineColumns([
  { key: 'name', label: 'Empresa', type: 'text', infoKey: 'common.company', width: 40 },
  { key: 'companyId', label: 'Identificador da empresa', type: 'text', infoKey: 'common.companyId', screen: false },
  { key: 'shardCode', label: 'Banco', type: 'text', infoKey: 'common.shard', screen: false },
  { key: 'adoptingUsers', label: 'Usuários no dia de pico', type: 'int', infoKey: 'features.topPeakUsers' },
  { key: 'eligibleUsers', label: 'Elegíveis de hoje', type: 'int', infoKey: 'features.topEligibleToday', screen: false },
  { key: 'adoption', label: '% dos elegíveis de hoje', type: 'metric', unit: 'pct', infoKey: 'features.topAdoption', value: cellMetric('adoption') },
]);



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piFeaturesModel.js